import React from "react";
import { useTranslation } from "react-i18next";
import {
  MaterialHsb,
  colorNumberToHsb,
  hsbToColorNumber,
  mergeColorIntoHsb,
} from "../../../../features/floorplan3d/materials/materialColorHsb";

type HsbColorSlidersProps = {
  color: number;
  onColorChange: (color: number) => void;
  onColorCommit?: () => void;
};

/**
 * SketchUp-like HSB sliders for solid (non-textured) material colors.
 * Keeps local HSB so hue stays editable when saturation is 0 (gray).
 */
const HsbColorSliders: React.FC<HsbColorSlidersProps> = ({
  color,
  onColorChange,
  onColorCommit,
}) => {
  const { t } = useTranslation();
  const [hsb, setHsb] = React.useState<MaterialHsb>(() =>
    colorNumberToHsb(color),
  );
  const lastEmittedColorRef = React.useRef(color);

  React.useEffect(() => {
    // Ignore echoes of our own emissions; sync when color changes externally.
    if (color === lastEmittedColorRef.current) return;
    lastEmittedColorRef.current = color;
    setHsb((previous) => mergeColorIntoHsb(color, previous));
  }, [color]);

  const updateChannel = (channel: keyof MaterialHsb, value: number) => {
    const next = { ...hsb, [channel]: value };
    const nextColor = hsbToColorNumber(next);
    lastEmittedColorRef.current = nextColor;
    setHsb(next);
    onColorChange(nextColor);
  };

  return (
    <div className="space-y-2 rounded-sm border border-gray-200 bg-gray-50 p-2">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[12px] font-medium text-gray-800">
          {t("propertyPanel.materialColor")}
        </div>
        <span
          className="h-4 w-4 shrink-0 rounded-sm border border-gray-300"
          style={{
            backgroundColor: `#${color.toString(16).padStart(6, "0")}`,
          }}
          aria-hidden
        />
      </div>
      <HsbSlider
        label={t("propertyPanel.hue")}
        value={hsb.h}
        min={0}
        max={360}
        unit="°"
        gradient="linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)"
        onChange={(value) => updateChannel("h", value)}
        onCommit={onColorCommit}
      />
      <HsbSlider
        label={t("propertyPanel.saturation")}
        value={hsb.s}
        min={0}
        max={100}
        unit="%"
        gradient={`linear-gradient(to right, #808080, hsl(${hsb.h}, 100%, 50%))`}
        onChange={(value) => updateChannel("s", value)}
        onCommit={onColorCommit}
      />
      <HsbSlider
        label={t("propertyPanel.brightness")}
        value={hsb.b}
        min={0}
        max={100}
        unit="%"
        gradient={`linear-gradient(to right, #000000, hsl(${hsb.h}, ${hsb.s}%, 50%))`}
        onChange={(value) => updateChannel("b", value)}
        onCommit={onColorCommit}
      />
    </div>
  );
};

const HsbSlider: React.FC<{
  label: string;
  value: number;
  min: number;
  max: number;
  unit: string;
  gradient: string;
  onChange: (value: number) => void;
  onCommit?: () => void;
}> = ({ label, value, min, max, unit, gradient, onChange, onCommit }) => (
  <label className="block">
    <div className="mb-1 flex items-center justify-between gap-2 text-[11px] font-medium text-gray-600">
      <span>{label}</span>
      <span className="tabular-nums text-gray-500">
        {value}
        {unit}
      </span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={1}
      value={value}
      aria-label={label}
      className="h-2 w-full cursor-pointer appearance-none rounded-sm border border-gray-300"
      style={{ backgroundImage: gradient }}
      onChange={(event) => onChange(Number(event.target.value))}
      onMouseUp={onCommit}
      onTouchEnd={onCommit}
      onBlur={onCommit}
      onKeyUp={(event) => {
        if (
          event.key === "ArrowLeft" ||
          event.key === "ArrowRight" ||
          event.key === "Home" ||
          event.key === "End"
        ) {
          onCommit?.();
        }
      }}
    />
  </label>
);

export default HsbColorSliders;
